import {Form, Formik, useField} from "formik";
import {invert, isUndefined} from "lodash";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {Col, Row} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {setMapSQL} from "../../../../actions/animalParametersActions";
import {mapSQL} from "../../../../constans/sqlSelects";
import useQueryCaller from "../../../../hooks/useQueryCaller";
import {
    getAnimalParametersLoading,
    getMapSQL,
} from "../../../../selectors/animalParametersSelector";
import {getAnimalCount} from "../../../../selectors/farmMapSelector";
import Button from "../../../basics/button/Button";
import Input from "../../../basics/input/Input";
import SQLInput from "../../../basics/input/sql-input/SQLInput";
import Select from "../../../basics/select/Select";
import "./_filter-animal-control.scss";
import {useLatestTechnologyGroups} from "../../../technology-group/select/TechnologyGroupSelect";
import {isFiniteNumber} from "../../../../utils/MathUtils";
import ButtonGroup from "../../../basics/button/button-group/ButtonGroup";

function FilterAnimalControlSelect({name, options}) {
    const {t} = useTranslation();

    const [input, , handlers] = useField(name);

    const onChange = useCallback(
        (value) => {
            handlers.setValue(value);
        },
        [handlers]
    );

    return (
        <Select
            value={input.value}
            onChange={onChange}
            options={options}
            placeholder={t("chooseValue")}
        />
    );
}

function FilterAnimalControlInput({name, placeholder}) {
    const [input, meta] = useField(name);

    return (
        <Input
            {...input}
            {...meta}
            onChangeWithEvent
            placeholder={placeholder}
        />
    );
}

function FilterAnimalControlTechnologyGroupInput({name, handleRemoteSubmit}) {
    const [group, setGroup] = useState(null);
    const groups = useLatestTechnologyGroups(50);

    const {t} = useTranslation();

    const [, , handlers] = useField(name);

    const onChange = useCallback(
        (newGroup) => {
            setGroup(newGroup ?? null);
            handlers.setValue(newGroup?.group ?? null);
        },
        [setGroup, handlers]
    );

    const selectedGroupId = group?.group ?? null;

    useEffect(() => {
        if (!selectedGroupId) {
            return;
        }
        const timeoutId = setTimeout(handleRemoteSubmit, 500);
        return () => clearTimeout(timeoutId);
    }, [selectedGroupId, handleRemoteSubmit]);

    const findSiblingElement = useCallback(
        (isNext) => {
            const currentIndex = groups.findIndex(
                (g) => g.name === group?.group
            );
            // DESC ORDER so next item has a lower index
            const newIndex = (currentIndex + (isNext ? -1 : 1)) % groups.length;
            return groups.at(newIndex)?.value;
        },
        [group, groups]
    );

    const setNext = useCallback(() => {
        onChange(findSiblingElement(true));
    }, [onChange, findSiblingElement]);

    const setPrev = useCallback(() => {
        onChange(findSiblingElement(false));
    }, [onChange, findSiblingElement]);

    const btnProps = useMemo(() => {
        return {
            type: "button",
            buttonColor: "menu-color",
        };
    }, []);

    return (
        <ButtonGroup>
            <Button
                {...btnProps}
                onClick={setPrev}
                icon={<i className={"fas fa-arrow-left"} />}
            />
            <Select
                value={group}
                onChange={onChange}
                options={groups}
                clearButton={false}
                placeholder={t("chooseValue")}
            />
            <Button
                {...btnProps}
                onClick={setNext}
                icon={<i className={"fas fa-arrow-right"} />}
            />
        </ButtonGroup>
    );
}

const FilterAnimalControlSQL = forwardRef(({name}, ref) => {
    const [input, , handlers] = useField(name);

    const onChange = useCallback(
        (value) => {
            handlers.setValue(value);
        },
        [handlers]
    );

    return (
        <SQLInput
            onChange={onChange}
            value={input.value}
            ref={ref}
            placeholder="SQL"
        />
    );
});

function FilterAnimalControlLegend({items}) {
    return (
        <div className="filter-animal-control-legend">
            {items.map((row, index) => (
                <div key={index}>
                    <div
                        className={`filter-animal-control-legend-color ${row.color}`}
                    />
                    <span>- {row.text}</span>
                </div>
            ))}
        </div>
    );
}

function FilterAnimalControlRepetitionLegend() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {text: t("repetitions", {count: 2}), color: "warning"},
            {text: t("moreThanRepetitions", {count: 2}), color: "error"},
        ],
        [t]
    );

    return <FilterAnimalControlLegend items={items} />;
}

function FilterAnimalControlTechnologyGroupLegend() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {text: t("animalInTechnologyGroup"), color: "success"},
            {text: t("animalRemovedFromTechnologyGroup"), color: "warning"},
            {
                text: t("animalRemovedFromTechnologyGroupBySeparation"),
                color: "info",
            },
        ],
        [t]
    );

    return <FilterAnimalControlLegend items={items} />;
}

function FilterAnimalControlParturitionLegend() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {text: t("parturitioned"), color: "info"},
            {text: t("beforeParturition"), color: "success"},
            {text: t("afterExpectedParturitionDate"), color: "error"},
        ],
        [t]
    );

    return <FilterAnimalControlLegend items={items} />;
}

export default function FilterAnimalControl() {
    const {t} = useTranslation();

    const animalCount = useSelector(getAnimalCount);

    const dispatch = useDispatch();

    const queryCaller = useQueryCaller();

    const sql = useSelector(getMapSQL);
    const isLoading = useSelector(getAnimalParametersLoading);

    const sqlInputRef = useRef();

    const onSubmit = useCallback(
        async (values) => {
            const invertedSQL = invert(mapSQL);
            const sql =
                values.sql === "custom" ? values.customValue : values.sql;
            const params = [];
            if (values.sql !== "custom") {
                // values get passed to the alasql params in the following order
                // `Value` is required
                // `Value1` `Value2` etc. is optional but the sequence is stopped when first isUndefined condition is met
                const maxAllowedValues = 3;
                const tryParse = (value) => {
                    if ([true, false].includes(value)) return value;
                    return isFiniteNumber(+value) ? +value : value;
                };
                for (let i = 0; i <= maxAllowedValues; i++) {
                    const key = `${invertedSQL[values.sql]}_Value${i ? i : ""}`;
                    const value = values[key];
                    if (i && isUndefined(value)) {
                        break;
                    }
                    params.push(tryParse(value));
                }
            }
            const result = await queryCaller(sql, params);
            dispatch(setMapSQL(sql, result, ...params));
            if (values.sql === "custom") {
                sqlInputRef.current.addSavedValue(values.customValue);
            }
        },
        [dispatch, queryCaller]
    );

    const options = useMemo(() => {
        return [
            {name: t("idleSows"), value: mapSQL.SOW_IDLE_DAYS},
            {
                name: t("sowsAfterExpectedParturition"),
                value: mapSQL.SOW_AFTER_EXPECTED_FARROWING,
            },
            {
                name: t("sowsBeforeParturition"),
                value: mapSQL.SOW_BEFORE_EXPECTED_FARROWING,
            },
            {name: t("sowsWithRepetitions"), value: mapSQL.SOW_REPETITIONS},
            {
                name: t("sowsBeforeAfterAndDelayedParturition"),
                value: mapSQL.SOW_BEFORE_DELAYED_AFTER_PARTURITION,
            },
            {name: t("transferDelay"), value: mapSQL.SOW_EXPECTED_TRANSFER},
            {
                name: t("sowsWithoutPigletOnBirthRoom"),
                value: mapSQL.SOW_WITHOUT_PIGLETS,
            },
            {name: t("sowsWithBrokenCycle"), value: mapSQL.SOW_WRONG_CYCLE},
            {name: t("sowsWithPiglets"), value: mapSQL.SOW_WITH_PIGLETS},
            {name: t("technologyGroup"), value: mapSQL.SOW_IN_TECHNOLOGY_GROUP},
            {
                name: t("technologyGroupHistorical"),
                value: mapSQL.SOW_IN_TECHNOLOGY_GROUP_HISTORICAL,
            },
            {name: t("userRolePicker.customAccess"), value: "custom"},
        ];
    }, [t]);

    const submitButtonRef = useRef(null);

    const handleRemoteSubmit = useCallback(() => {
        submitButtonRef.current?.click();
    }, [submitButtonRef]);

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={{customValue: sql, sql: mapSQL.SOW_IDLE_DAYS}}>
            {(props) => (
                <Form>
                    <Row className="filter-animal-control">
                        <Col
                            lg={"auto"}
                            xs={12}
                            className="filter-animal-control-input">
                            <FilterAnimalControlSelect
                                options={options}
                                name="sql"
                            />
                        </Col>
                        <Col
                            lg={"auto"}
                            xs={12}
                            className="filter-animal-control-input">
                            {props.values.sql === mapSQL.SOW_IDLE_DAYS && (
                                <FilterAnimalControlInput
                                    name={`SOW_IDLE_DAYS_Value`}
                                    placeholder={t("grid.idleDays")}
                                />
                            )}
                            {props.values.sql ===
                                mapSQL.SOW_AFTER_EXPECTED_FARROWING && (
                                <FilterAnimalControlInput
                                    name={`SOW_AFTER_EXPECTED_FARROWING_Value`}
                                    placeholder={t(
                                        "daysAfterExpectedParturition"
                                    )}
                                />
                            )}
                            {props.values.sql ===
                                mapSQL.SOW_BEFORE_EXPECTED_FARROWING && (
                                <FilterAnimalControlInput
                                    name={`SOW_BEFORE_EXPECTED_FARROWING_Value`}
                                    placeholder={t("daysBeforeParturition")}
                                />
                            )}
                            {props.values.sql ===
                                mapSQL.SOW_IN_TECHNOLOGY_GROUP && (
                                <>
                                    <FilterAnimalControlTechnologyGroupInput
                                        handleRemoteSubmit={handleRemoteSubmit}
                                        name={"SOW_IN_TECHNOLOGY_GROUP_Value"}
                                        placeholder={t(
                                            "selectedTechnologyGroup"
                                        )}
                                    />
                                </>
                            )}
                            {props.values.sql ===
                                mapSQL.SOW_IN_TECHNOLOGY_GROUP_HISTORICAL && (
                                <>
                                    <FilterAnimalControlTechnologyGroupInput
                                        handleRemoteSubmit={handleRemoteSubmit}
                                        name={
                                            "SOW_IN_TECHNOLOGY_GROUP_HISTORICAL_Value"
                                        }
                                        placeholder={t(
                                            "selectedTechnologyGroup"
                                        )}
                                    />
                                </>
                            )}
                            {props.values.sql === "custom" && (
                                <FilterAnimalControlSQL
                                    ref={sqlInputRef}
                                    name="customValue"
                                />
                            )}
                        </Col>
                        <Col
                            lg={"auto"}
                            xs={12}
                            className="filter-animal-control-submit justify-content-center justify-content-lg-end text-end">
                            <Button
                                buttonRef={submitButtonRef}
                                buttonColor="success"
                                disabled={isLoading}
                                className="me-3"
                                isLoading={props.isSubmitting}>
                                {t("show")}
                            </Button>
                            <i className="fa-solid fa-pig me-1" />
                            <span>
                                {animalCount > 1000 ? "+999" : animalCount}
                            </span>
                        </Col>
                        <Col
                            xs={12}
                            className="filter-animal-control-input small">
                            {props.values.sql === mapSQL.SOW_REPETITIONS && (
                                <FilterAnimalControlRepetitionLegend />
                            )}
                            {props.values.sql ===
                                mapSQL.SOW_BEFORE_DELAYED_AFTER_PARTURITION && (
                                <FilterAnimalControlParturitionLegend />
                            )}
                            {props.values.sql ===
                                mapSQL.SOW_IN_TECHNOLOGY_GROUP_HISTORICAL && (
                                <FilterAnimalControlTechnologyGroupLegend />
                            )}
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
}
